import { FormInstance, message } from 'antd';
import { defaultDeviceModels } from './DynamicFormCard.utils';
import PIDEditStore, { ActiveMenuState, MenuType } from '@/store/PIDEditing/PIDEditStore';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { UnitMeasurementSystem, DeviceKind } from '@/constants/enums';
import { DUMMY_MODEL_IDS } from '@/comp/MapBuilder/nodes/nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';

export interface ControllerOption {
  value: string;
  label: string;
}

export interface ModelOption {
  value: string;
  label: string;
  controllerId?: string | number | null;
  deviceKind?: string;
}

interface DeviceFormValues {
  name?: string;
  model?: string;
  controller?: string;
  pipeDiameter?: number;
  pipeMaterial?: string;
}

interface DeviceUpdatePayload {
  name: string;
  deviceModelId: string;
  controllerId: string;
  attributes?: {
    pipeDiameter?: number;
    pipeMaterial?: string;
  };
}

// Pipe diameter conversion utilities using tvalue
const convertPipeDiameterToMm = (
  value: number,
  measurementSystem?: string,
  tvalue?: (value: number, unit: string, toSystem: string, fromSystem: string) => number,
): number => {
  if (measurementSystem?.toLowerCase() === UnitMeasurementSystem.Imperial && tvalue) {
    return tvalue(value, 'inch', UnitMeasurementSystem.Metric, UnitMeasurementSystem.Imperial);
  }
  return value; // Already in mm
};

const convertPipeDiameterFromMm = (
  value: number,
  measurementSystem?: string,
  tvalue?: (value: number, unit: string, toSystem: string, fromSystem: string) => number,
): number => {
  if (measurementSystem?.toLowerCase() === UnitMeasurementSystem.Imperial && tvalue) {
    return tvalue(value, 'mm', UnitMeasurementSystem.Imperial, UnitMeasurementSystem.Metric);
  }
  return value; // Already in mm
};

export interface DeviceDetailsUtils {
  resetFormState: () => void;
  updateControllerState: (controller: { id: string; name: string } | null, t: (key: string) => string) => void;
  initializeDeviceDetails: (
    entity: Entity,
    modelSpecPromise: Promise<ModelOption[]>,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
    tvalue?: (value: number, unit: string, toSystem: string, fromSystem: string) => number,
    measurementSystem?: string,
  ) => Promise<void>;
  initializeCreateDevice: (
    deviceType: string,
    modelSpecPromise: Promise<ModelOption[]>,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
  ) => Promise<void>;
  updateControllerDetails: (
    controllerId: string | number | null,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
  ) => Promise<void>;
  createDevice: (
    values: DeviceFormValues,
    activeMenu: ActiveMenuState,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
  ) => Promise<void>;
  handleUpdateDevice: (
    values: DeviceFormValues,
    activeMenu: ActiveMenuState,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
    tvalue?: (value: number, unit: string, toSystem: string, fromSystem: string) => number,
    measurementSystem?: string,
  ) => Promise<void>;
  handleFormChange: (
    changedValues: DeviceFormValues,
    modelSpecOptions: ModelOption[],
    pidEditStore: PIDEditStore,
  ) => void;
}

export const calculateCardPosition = (
  activeMenu: ActiveMenuState | null,
  cardRef: React.RefObject<HTMLDivElement>,
): { x: number; y: number } => {
  if (!activeMenu) return { x: 0, y: 0 };

  let newPos = activeMenu.position;
  if (cardRef.current) {
    const parent = cardRef.current.parentElement;
    if (parent) {
      const parentRect = parent.getBoundingClientRect();
      const cardWidth = 224;
      const cardHeight = cardRef.current.offsetHeight;
      const BOUNDARY_OFFSET = 240;

      const maxY = parentRect.height - cardHeight - BOUNDARY_OFFSET;

      let adjX = newPos.x;
      if (newPos.x + cardWidth > parentRect.width - BOUNDARY_OFFSET) {
        adjX = newPos.x - cardWidth;
        adjX = Math.max(BOUNDARY_OFFSET, adjX);
      } else {
        adjX = Math.max(newPos.x, BOUNDARY_OFFSET);
      }

      const adjY = Math.min(Math.max(newPos.y, BOUNDARY_OFFSET), maxY);
      newPos = { x: adjX, y: adjY };
    }
  }
  return newPos;
};

export const createDeviceDetailsUtils = (
  form: FormInstance,
  setModelSpecOptions: React.Dispatch<React.SetStateAction<ModelOption[]>>,
  setControllerPlaceholder: React.Dispatch<React.SetStateAction<string>>,
  setControllerOptions: React.Dispatch<React.SetStateAction<ControllerOption[]>>,
): DeviceDetailsUtils => {
  const resetFormState = () => {
    form.resetFields();
    setModelSpecOptions([]);
    setControllerPlaceholder('No Controller');
    setControllerOptions([]);
  };

  const updateControllerState = (controller: { id: string; name: string } | null, t: (key: string) => string) => {
    if (controller) {
      const controllerName = controller.name;
      const controllerId = String(controller.id);
      setControllerPlaceholder(controllerName);
      form.setFieldsValue({ controller: controllerId });
    } else {
      setControllerPlaceholder(t('No Controller'));
      setControllerOptions([]);
      form.setFieldsValue({ controller: undefined });
    }
  };

  const initializeDeviceDetails = async (
    entity: Entity,
    modelSpecPromise: Promise<ModelOption[]>,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
    tvalue?: (value: number, unit: string, toSystem: string, fromSystem: string) => number,
    measurementSystem?: string,
  ) => {
    try {
      const deviceDetails = await pidEditStore.getDeviceDetails(entity.deviceId);

      if (!deviceDetails) {
        message.error(t('Failed to load device details.'));
        return;
      }

      const { controllerId, controllerName, deviceModelId, pipeDiameter, pipeMaterial, deviceKind } = deviceDetails;

      await modelSpecPromise;

      const deviceControllerId = controllerId ? String(controllerId) : undefined;
      const deviceControllerName = controllerName || t('No Controller');
      const deviceControllers = pidEditStore.deviceControllers;

      setControllerPlaceholder(deviceControllerName);
      setControllerOptions(deviceControllers || []);

      form.setFieldsValue({
        name: entity.name,
        model: deviceModelId ? String(deviceModelId) : undefined,
        controller: deviceControllerId,
        ...(entity.deviceType === DeviceType.FlowSensor && {
          pipeDiameter: convertPipeDiameterFromMm(pipeDiameter, measurementSystem, tvalue),
          pipeMaterial: pipeMaterial,
        }),
      });

      pidEditStore.setDeviceState({
        name: entity.name,
        deviceModelId: deviceModelId,
        controllerId: controllerId,
        deviceKind: deviceKind as DeviceKind,
      });
    } catch (error) {
      console.error('Failed to initialize device details:', error);
      message.error(t('Failed to load device details.'));
    }
  };

  const initializeCreateDevice = async (
    deviceType: DeviceType,
    modelSpecPromise: Promise<ModelOption[]>,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
  ) => {
    try {
      const defaultName = pidEditStore.defaultCreateDeviceName(deviceType, t);
      form.setFieldsValue({ name: defaultName });
      pidEditStore.setDeviceState({ name: defaultName });
      const options = await modelSpecPromise;
      const defaultModelId = defaultDeviceModels[deviceType];
      const deviceControllers = pidEditStore.deviceControllers;
      setControllerOptions(deviceControllers || []);

      if (defaultModelId) {
        const modelExists = options.some(o => o.value === defaultModelId);
        if (modelExists) {
          form.setFieldsValue({ model: defaultModelId });
          const selectedModel = options.find(option => String(option.value) === String(defaultModelId));
          if (selectedModel) {
            const { value: deviceModelId, controllerId, deviceKind } = selectedModel;
            pidEditStore.setDeviceState({
              deviceModelId,
              controllerId: controllerId != null ? String(controllerId) : null,
              deviceKind: deviceKind as DeviceKind,
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to initialize create device:', error);
      message.error(t('Failed to initialize device creation.'));
    }
  };

  const updateControllerDetails = async (
    controllerId: string | number | null,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
  ) => {
    const isValidControllerId =
      controllerId != null && controllerId !== '' && controllerId !== 'no-controller' && controllerId !== 'undefined';

    if (isValidControllerId) {
      try {
        const controller = await pidEditStore.getDeviceController(String(controllerId));
        updateControllerState(controller, t);
      } catch (error) {
        console.error('Failed to fetch controller:', error);
        setControllerPlaceholder(t('Error fetching controller'));
        setControllerOptions([]);
        form.setFieldsValue({ controller: undefined });
      }
    } else {
      setControllerPlaceholder(t('No Controller'));
      setControllerOptions([]);
      form.setFieldsValue({ controller: undefined });
    }
  };

  const createDevice = async (
    _values: DeviceFormValues,
    activeMenu: ActiveMenuState,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
  ) => {
    try {
      await pidEditStore.createDevice(activeMenu?.context?.deviceType);
      message.success(t('Device created successfully'));
      pidEditStore.hideMenu();
    } catch (error) {
      console.error('Failed to create device:', error);
      message.error(typeof error === 'string' ? error : JSON.stringify(error));
    }
  };

  const handleUpdateDevice = async (
    values: DeviceFormValues,
    activeMenu: ActiveMenuState,
    pidEditStore: PIDEditStore,
    t: (key: string) => string,
    tvalue?: (value: number, unit: string, toSystem: string, fromSystem: string) => number,
    measurementSystem?: string,
  ) => {
    const { name, deviceModelId } = pidEditStore.deviceState;
    const isNoMatchingSpec = Object.values(DUMMY_MODEL_IDS).includes(deviceModelId as DUMMY_MODEL_IDS);
    const payload: DeviceUpdatePayload = {
      name: name || values.name,
      deviceModelId,
      controllerId: isNoMatchingSpec ? '' : values.controller,
    };

    if (activeMenu.context.entity?.deviceType === DeviceType.FlowSensor) {
      payload.attributes = {};

      if (values.pipeDiameter !== undefined && values.pipeDiameter !== null && String(values.pipeDiameter) !== '') {
        const diameterInMm = convertPipeDiameterToMm(Number(values.pipeDiameter), measurementSystem, tvalue);

        payload.attributes.pipeDiameter = diameterInMm;
      }

      if (values.pipeMaterial !== undefined && values.pipeMaterial !== null && values.pipeMaterial !== '') {
        payload.attributes.pipeMaterial = values.pipeMaterial;
      }
    }

    try {
      await pidEditStore.updateDevice(String(activeMenu.context.entity.deviceId), payload);
      message.success(t('Device details updated successfully'));
      pidEditStore.hideMenu();
    } catch (error) {
      console.error('Failed to update device:', error);
      message.error(typeof error === 'string' ? error : JSON.stringify(error));
    }
  };

  const handleFormChange = (
    changedValues: DeviceFormValues,
    modelSpecOptions: ModelOption[],
    pidEditStore: PIDEditStore,
  ) => {
    if ('name' in changedValues) {
      pidEditStore.setDeviceState({ name: changedValues.name });
    }
    if ('model' in changedValues) {
      const selectedModel = modelSpecOptions.find(option => String(option.value) === String(changedValues.model));
      if (selectedModel) {
        const { value: deviceModelId, controllerId, deviceKind } = selectedModel;
        pidEditStore.setDeviceState({
          deviceModelId,
          controllerId: controllerId != null ? String(controllerId) : null,
          deviceKind: deviceKind as DeviceKind,
        });
        // Immediately clear the controller field when model changes to prevent stale values
        form.setFieldsValue({ controller: undefined });
      } else {
        pidEditStore.setDeviceState({ deviceModelId: null, controllerId: null, deviceKind: null });
        form.setFieldsValue({ controller: undefined });
      }
    }
  };

  return {
    resetFormState,
    updateControllerState,
    initializeDeviceDetails,
    initializeCreateDevice,
    updateControllerDetails,
    createDevice,
    handleUpdateDevice,
    handleFormChange,
  };
};

export const onCancelHandler = (
  form: FormInstance,
  pidEditStore: PIDEditStore,
  setControllerPlaceholder: React.Dispatch<React.SetStateAction<string>>,
  setControllerOptions: React.Dispatch<React.SetStateAction<ControllerOption[]>>,
  t: (key: string) => string,
) => {
  form.resetFields();
  pidEditStore.setDeviceState({ name: '', deviceModelId: null, controllerId: null, deviceKind: null });
  setControllerPlaceholder(t('No Controller'));
  setControllerOptions([]);
  pidEditStore.hideMenu();
};

export const isMenuTypeCreateDevice = (menuType: MenuType) => menuType === MenuType.CreateDevice;

export const isMenuTypeDeviceDetails = (menuType: MenuType, isAssetDeviceType: boolean) =>
  menuType === MenuType.DeviceDetails && !isAssetDeviceType;
